/**
 * EZ Gallery — combinable color + project type filters with batched reveal.
 *
 * Every card is rendered by ez-gallery.liquid, so filtering is a show/hide pass over the
 * DOM rather than a server round trip. Items carry data-color and data-ptype; an empty
 * value means untagged, which only ever shows under "All".
 */
(function () {
  const FACETS = ['color', 'ptype'];

  function initDisclosure(dropdown) {
    const toggle = dropdown.querySelector('[data-ez-gallery-filter-toggle]');
    const panel = dropdown.querySelector('[data-ez-gallery-filter-panel]');
    if (!toggle || !panel) return;

    toggle.addEventListener('click', () => {
      const open = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
      panel.classList.toggle('is-collapsed', open);
    });
  }

  class GalleryFilters {
    constructor(root) {
      this.root = root;
      this.grid = root.querySelector('[data-ez-gallery-grid]');
      if (!this.grid) return;

      this.filtersRoot = root.querySelector('[data-ez-gallery-filters]');
      this.loadMoreWrap = root.querySelector('[data-ez-gallery-load-more-wrap]');
      this.loadMoreBtn = root.querySelector('[data-ez-gallery-load-more]');
      this.noResults = root.querySelector('[data-ez-gallery-no-results]');
      this.resetBtn = root.querySelector('[data-ez-gallery-reset]');
      this.statusEl = root.querySelector('[data-ez-gallery-status]');

      this.items = Array.from(this.grid.querySelectorAll('[data-ez-gallery-item]'));
      if (!this.items.length) return;

      this.buttons = Array.from(root.querySelectorAll('[data-ez-gallery-filter]'));
      this.batchSize = parseInt(this.grid.dataset.batchSize, 10) || 24;
      this.revealed = this.batchSize;
      this.state = { color: '', ptype: '' };

      this.readUrl();
      this.bind();
      this.apply({ updateUrl: false });
    }

    availableValues(facet) {
      return this.buttons
        .filter((button) => button.dataset.facet === facet && button.dataset.value)
        .map((button) => button.dataset.value);
    }

    readUrl() {
      const params = new URLSearchParams(window.location.search);

      FACETS.forEach((facet) => {
        const value = (params.get(facet) || '').trim().toLowerCase();
        if (value && this.availableValues(facet).includes(value)) {
          this.state[facet] = value;
        }
      });
    }

    bind() {
      this.root.querySelectorAll('[data-ez-gallery-filter-dropdown]').forEach(initDisclosure);

      this.buttons.forEach((button) => {
        button.addEventListener('click', () => {
          const { facet, value } = button.dataset;
          if (!FACETS.includes(facet)) return;
          // aria-disabled rather than disabled, so unavailable chips stay reachable by
          // keyboard and are announced instead of vanishing from the tab order.
          if (button.getAttribute('aria-disabled') === 'true') return;

          // Clicking the active chip clears that facet back to All.
          this.state[facet] = this.state[facet] === value ? '' : value;
          this.revealed = this.batchSize;
          this.apply({ updateUrl: true });
          this.collapsePanels();
        });
      });

      if (this.resetBtn) {
        this.resetBtn.addEventListener('click', () => {
          FACETS.forEach((facet) => {
            this.state[facet] = '';
          });
          this.revealed = this.batchSize;
          this.apply({ updateUrl: true });
          this.collapsePanels();
          // The reset button hides itself once cleared, so hand focus to the status line
          // rather than dropping it on the body.
          this.statusEl?.focus({ preventScroll: true });
        });
      }

      if (this.loadMoreBtn) {
        this.loadMoreBtn.addEventListener('click', () => {
          const firstNew = Math.min(this.matches.length, this.revealed);
          this.revealed += this.batchSize;
          this.apply({ updateUrl: false, focusIndex: firstNew });
        });
      }

      // The lightbox can walk past the revealed batch; keep the grid in step behind it.
      this.grid.addEventListener('ez-gallery:reveal', (event) => {
        const item = event.target.closest('[data-ez-gallery-item]');
        if (!item) return;

        const position = this.matches.indexOf(item);
        if (position < 0 || position < this.revealed) return;

        this.revealed = Math.ceil((position + 1) / this.batchSize) * this.batchSize;
        this.apply({ updateUrl: false });
      });
    }

    collapsePanels() {
      this.root.querySelectorAll('[data-ez-gallery-filter-panel]').forEach((panel) => {
        panel.classList.add('is-collapsed');
      });
      this.root.querySelectorAll('[data-ez-gallery-filter-toggle]').forEach((toggle) => {
        toggle.setAttribute('aria-expanded', 'false');
      });
    }

    itemMatches(item, state) {
      return FACETS.every((facet) => {
        const wanted = state[facet];
        if (!wanted) return true;
        return (item.dataset[facet] || '') === wanted;
      });
    }

    apply({ updateUrl = false, focusIndex = -1 } = {}) {
      this.matches = this.items.filter((item) => this.itemMatches(item, this.state));
      const matchSet = new Set(this.matches);

      this.items.forEach((item) => {
        const matched = matchSet.has(item);
        item.dataset.filteredOut = matched ? 'false' : 'true';
        if (!matched) item.hidden = true;
      });

      this.matches.forEach((item, index) => {
        item.hidden = index >= this.revealed;
      });

      this.updateChips();
      this.updateLoadMore();
      this.updateStatus();

      if (this.noResults) this.noResults.hidden = this.matches.length > 0;

      if (updateUrl) this.updateUrl();
      if (focusIndex >= 0) this.focusItemAt(focusIndex);
    }

    focusItemAt(index) {
      const item = this.matches[index];
      const focusTarget = item?.querySelector('.ez-gallery-card__toggle');
      if (focusTarget) focusTarget.focus({ preventScroll: true });
    }

    /**
     * Counts are cross-filtered: with Black selected, project type counts describe the
     * black projects only, so a chip never promises results it cannot deliver.
     */
    countFor(facet, value) {
      const probe = { ...this.state, [facet]: value };
      return this.items.filter((item) => this.itemMatches(item, probe)).length;
    }

    updateChips() {
      this.buttons.forEach((button) => {
        const { facet, value } = button.dataset;
        if (!FACETS.includes(facet)) return;

        const active = this.state[facet] === value;
        const count = this.countFor(facet, value);
        const isChip = button.classList.contains('ez-gallery__filter-link');

        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');

        const countEl = button.querySelector('.ez-gallery__filter-count');
        if (countEl) countEl.textContent = `(${count})`;

        const unavailable = count === 0 && !active;
        button.classList.toggle('is-unavailable', unavailable);
        button.setAttribute('aria-disabled', unavailable ? 'true' : 'false');

        if (isChip) return;
        button.setAttribute('aria-current', active ? 'true' : 'false');
      });

      this.root.querySelectorAll('[data-ez-gallery-filter-group]').forEach((group) => {
        const facet = group.dataset.ezGalleryFilterGroup;
        const summary = group.querySelector('[data-ez-gallery-filter-summary]');
        if (!summary) return;

        const activeButton =
          group.querySelector(`.ez-gallery__filter-panel-link[data-value="${this.state[facet]}"]`) ||
          group.querySelector('.ez-gallery__filter-panel-link[data-value=""]');
        if (!activeButton) return;

        const label = activeButton.querySelector('span')?.textContent?.trim() || '';
        summary.innerHTML = '';
        summary.append(label + ' ');

        const count = document.createElement('span');
        count.className = 'ez-gallery__filter-count';
        count.setAttribute('aria-hidden', 'true');
        count.textContent = `(${this.countFor(facet, this.state[facet])})`;
        summary.append(count);
      });
    }

    get isFiltered() {
      return FACETS.some((facet) => Boolean(this.state[facet]));
    }

    updateStatus() {
      if (this.resetBtn) this.resetBtn.hidden = !this.isFiltered;
      if (!this.statusEl) return;

      const total = this.items.length;
      const matched = this.matches.length;
      const shown = Math.min(matched, this.revealed);
      const noun = matched === 1 ? 'project' : 'projects';

      if (!matched) {
        this.statusEl.textContent = `No projects match these filters. Showing 0 of ${total}.`;
        return;
      }

      this.statusEl.textContent = this.isFiltered
        ? `Showing ${shown} of ${matched} ${noun}, filtered from ${total}.`
        : `Showing ${shown} of ${total} ${noun}.`;
    }

    updateLoadMore() {
      if (!this.loadMoreWrap) return;

      const remaining = this.matches.length - this.revealed;
      this.loadMoreWrap.hidden = remaining <= 0;

      if (this.loadMoreBtn) {
        this.loadMoreBtn.setAttribute(
          'aria-label',
          `Load ${Math.min(remaining, this.batchSize)} more of ${this.matches.length} projects`
        );
      }
    }

    updateUrl() {
      const params = new URLSearchParams(window.location.search);

      FACETS.forEach((facet) => {
        if (this.state[facet]) {
          params.set(facet, this.state[facet]);
        } else {
          params.delete(facet);
        }
      });

      const query = params.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
    }
  }

  function init() {
    document.querySelectorAll('[data-ez-gallery-grid]').forEach((grid) => {
      const root = grid.closest('.shopify-section') || document;
      if (root.ezGalleryFilters) return;
      root.ezGalleryFilters = new GalleryFilters(root);
    });

    // Teaser grids have filter dropdowns but no filterable grid of their own.
    document.querySelectorAll('[data-ez-gallery-filter-dropdown]').forEach((dropdown) => {
      if (dropdown.closest('[data-ez-gallery-filters]')) return;
      initDisclosure(dropdown);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
